import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { InsightsState } from 'app-redux/InsightsState';
import * as dto from 'client/dto';
import { nameof } from 'utils/typeHelpers';

import { loadAllDiseasesSuccess } from 'diseases/diseases.slice';
import { loadAllLocationsSuccess } from 'locations/locations.slice';

import { RiskOverviewFilterItem } from './RiskOverviewFilterModel';
import { RiskOverviewPageSize } from './riskOverviewHelper';

export type RiskOverviewState = {
  isLoading: boolean;
  isLoadingRiskIncidenceByDiseaseIdAndLocationId: Record<number, Record<number, boolean>>;
  isLoadingImportationSources: Record<number, Record<number, boolean>>;
  hasError: boolean;
  shouldDisplayLoadMore: boolean;
  entriesByActivityIdAndLocationId: Record<number, Record<number, dto.RiskFeedEntryModel>>;
  proximalCasesByLocationIdAndDiseaseId: Record<
    number,
    Record<number, dto.TemporalActivityModel[]>
  >;
  sortedRiskFeedPositions: dto.RiskFeedPosition[];
  locationNamesById: Record<number, string>;
  diseaseNamesById: Record<number, string>;
  filteredItems: RiskOverviewFilterItem[];
  riskFeedSortType: dto.RiskFeedSortType;
  importationSourcesByActivityIdAndLocationId: Record<
    number,
    Record<number, dto.GetGeonameModel[]>
  >;
  previousFrontPageStateId: string;
  nestedCaseCountsByDiseaseIdAndLocationId: Record<
    number,
    Record<number, dto.HierarchicalCaseCountModel>
  >;
  lastCheckByDiseaseIdAndLocationId: Record<number, Record<number, string>>;
  hasIncidenceCaseCounts: Record<number, Record<number, boolean>>;
  downloadingPngs: boolean;
};

export const InitialRiskOverviewState: RiskOverviewState = {
  isLoading: false,
  isLoadingRiskIncidenceByDiseaseIdAndLocationId: {},
  isLoadingImportationSources: {},
  hasError: false,
  shouldDisplayLoadMore: true,
  entriesByActivityIdAndLocationId: {},
  proximalCasesByLocationIdAndDiseaseId: {},
  sortedRiskFeedPositions: [],
  locationNamesById: {},
  diseaseNamesById: {},
  filteredItems: [],
  riskFeedSortType: dto.RiskFeedSortType.MostAtRisk,
  importationSourcesByActivityIdAndLocationId: {},
  previousFrontPageStateId: null,
  nestedCaseCountsByDiseaseIdAndLocationId: {},
  lastCheckByDiseaseIdAndLocationId: {},
  hasIncidenceCaseCounts: {},
  downloadingPngs: false,
};

const riskOverviewSlice = createSlice({
  name: nameof<InsightsState>('riskOverview'),
  initialState: InitialRiskOverviewState,
  reducers: {
    startExportingPngs: state => {
      state.downloadingPngs = true;
    },
    finishExportingPngs: state => {
      state.downloadingPngs = false;
    },
    loadRiskOverviewStart: state => {
      state.isLoading = true;
      state.hasError = false;
    },
    loadRiskOverviewSuccess: (state, action: PayloadAction<dto.RiskFeedEntryModel[]>) => {
      const riskFeedEntries = action.payload;
      riskFeedEntries.forEach(entry => {
        if (!(entry.diseaseActivityId in state.entriesByActivityIdAndLocationId)) {
          state.entriesByActivityIdAndLocationId[entry.diseaseActivityId] = {};
        }
        state.entriesByActivityIdAndLocationId[entry.diseaseActivityId][entry.locationId] = entry;
        state.sortedRiskFeedPositions.push({
          diseaseActivityId: entry.diseaseActivityId,
          locationId: entry.locationId,
        });
      });
      state.isLoading = false;
      state.shouldDisplayLoadMore = riskFeedEntries.length >= RiskOverviewPageSize;
    },
    loadRiskOverviewError: state => {
      state.isLoading = false;
      state.hasError = true;
    },
    clearRiskOverview: state => {
      state.shouldDisplayLoadMore = true;
      state.entriesByActivityIdAndLocationId = {};
      state.sortedRiskFeedPositions = [];
    },
    filterRiskOverviewFeed: (state, action: PayloadAction<{ items: RiskOverviewFilterItem[] }>) => {
      state.filteredItems = action.payload.items;
    },
    resetRiskOverviewSortAndFilters: state => {
      state.riskFeedSortType = InitialRiskOverviewState.riskFeedSortType;
      state.filteredItems = [];
    },
    sortRiskOverviewFeed: (state, action: PayloadAction<dto.RiskFeedSortType>) => {
      state.riskFeedSortType = action.payload;
    },
    loadImportationSourcesStart: (
      state,
      action: PayloadAction<{
        diseaseActivityId: number;
        locationId: number;
      }>
    ) => {
      const { diseaseActivityId, locationId } = action.payload;
      state.isLoadingImportationSources[diseaseActivityId] = {
        ...state.isLoadingRiskIncidenceByDiseaseIdAndLocationId[diseaseActivityId],
        [locationId]: true,
      };
    },
    loadImportationSourcesError: (
      state,
      action: PayloadAction<{
        diseaseActivityId: number;
        locationId: number;
      }>
    ) => {
      const { diseaseActivityId, locationId } = action.payload;
      state.isLoadingImportationSources[diseaseActivityId] = {
        ...state.isLoadingRiskIncidenceByDiseaseIdAndLocationId[diseaseActivityId],
        [locationId]: false,
      };
    },
    loadImportationSourcesSuccess: (
      state,
      action: PayloadAction<{
        diseaseActivityId: number;
        locationId: number;
        sources: dto.GetGeonameModel[];
      }>
    ) => {
      const importationSources = action.payload.sources;
      const diseaseActivityId = action.payload.diseaseActivityId;
      const locationId = action.payload.locationId;
      state.importationSourcesByActivityIdAndLocationId[diseaseActivityId] = {
        ...state.importationSourcesByActivityIdAndLocationId[diseaseActivityId],
        [locationId]: importationSources,
      };
      state.isLoadingImportationSources[diseaseActivityId] = {
        ...state.isLoadingRiskIncidenceByDiseaseIdAndLocationId[diseaseActivityId],
        [locationId]: false,
      };
    },
    setFrontPageStateId: (state, action: PayloadAction<string>) => {
      state.previousFrontPageStateId = action.payload;
    },
    loadRiskFeedIncidenceStart: (
      state,
      action: PayloadAction<{
        diseaseId: number;
        locationId: number;
      }>
    ) => {
      const { diseaseId, locationId } = action.payload;
      state.isLoadingRiskIncidenceByDiseaseIdAndLocationId[diseaseId] = {
        ...state.isLoadingRiskIncidenceByDiseaseIdAndLocationId[diseaseId],
        [locationId]: true,
      };
    },
    loadRiskFeedIncidenceSuccess: (
      state,
      action: PayloadAction<{
        locationId: number;
        diseaseId: number;
        riskFeedIncidence: dto.RiskFeedIncidenceModel;
      }>
    ) => {
      const { locationId, diseaseId, riskFeedIncidence } = action.payload;
      state.proximalCasesByLocationIdAndDiseaseId[diseaseId] = {
        ...state.proximalCasesByLocationIdAndDiseaseId[diseaseId],
        [locationId]: riskFeedIncidence.temporalActivityList,
      };
      state.nestedCaseCountsByDiseaseIdAndLocationId[diseaseId] = {
        ...state.nestedCaseCountsByDiseaseIdAndLocationId[diseaseId],
        [locationId]: riskFeedIncidence.caseCounts,
      };
      state.lastCheckByDiseaseIdAndLocationId[diseaseId] = {
        ...state.lastCheckByDiseaseIdAndLocationId[diseaseId],
        [locationId]: riskFeedIncidence.lastUpdatedDate,
      };
      state.hasIncidenceCaseCounts[diseaseId] = {
        ...state.hasIncidenceCaseCounts[diseaseId],
        [locationId]: riskFeedIncidence.hasReportedData,
      };
      state.isLoadingRiskIncidenceByDiseaseIdAndLocationId[diseaseId] = {
        ...state.isLoadingRiskIncidenceByDiseaseIdAndLocationId[diseaseId],
        [locationId]: false,
      };
    },
    loadRiskFeedIncidenceError: (
      state,
      action: PayloadAction<{
        diseaseId: number;
        locationId: number;
      }>
    ) => {
      const { diseaseId, locationId } = action.payload;
      state.isLoadingRiskIncidenceByDiseaseIdAndLocationId[diseaseId] = {
        ...state.isLoadingRiskIncidenceByDiseaseIdAndLocationId[diseaseId],
        [locationId]: false,
      };
    },
  },
  extraReducers: {
    [loadAllLocationsSuccess.type]: (state, action: PayloadAction<dto.GetGeonameModel[]>) => {
      state.locationNamesById = {};
      action.payload.forEach(location => {
        state.locationNamesById[location.geonameId] = location.name;
      });
      const updatedLocationIds = action.payload.map(x => x.geonameId);
      state.filteredItems = state.filteredItems.filter(
        x => (x.type === 'location' && updatedLocationIds.includes(x.id)) || x.type === 'disease'
      );
    },
    [loadAllDiseasesSuccess.type]: (
      state,
      action: PayloadAction<dto.DiseaseInformationModel[]>
    ) => {
      state.diseaseNamesById = {};
      action.payload.forEach(disease => {
        state.diseaseNamesById[disease.id] = disease.name;
      });
      const updatedDiseaseIds = action.payload.map(x => x.id);
      state.filteredItems = state.filteredItems.filter(
        x => (x.type === 'disease' && updatedDiseaseIds.includes(x.id)) || x.type === 'location'
      );
    },
  },
});

const { actions, reducer } = riskOverviewSlice;
export const {
  startExportingPngs,
  finishExportingPngs,
  loadRiskOverviewStart,
  loadRiskOverviewSuccess,
  loadRiskOverviewError,
  clearRiskOverview,
  filterRiskOverviewFeed,
  sortRiskOverviewFeed,
  loadImportationSourcesStart,
  loadImportationSourcesError,
  loadImportationSourcesSuccess,
  setFrontPageStateId,
  resetRiskOverviewSortAndFilters,
  loadRiskFeedIncidenceStart,
  loadRiskFeedIncidenceSuccess,
  loadRiskFeedIncidenceError,
} = actions;
export default reducer;
